import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { OrgNav } from '../components/OrgNav.js';
import { TeachingSummary } from '../components/TeachingSummary.js';
import { Card, ErrorBanner, Loading, Spinner, StatusBadge, UsageMeter } from '../components/ui.js';
import {
  api,
  type BillingStatus,
  type InvoicePreview,
  type Organization,
  type Subscription,
  type UsageEntry,
} from '../lib/api.js';
import { formatDate, formatMoney, formatNumber, formatRelative, pluralise } from '../lib/format.js';
import { useClientConfig } from '../lib/config.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * The institute's home page: the next class first, then billing if billing is on.
 *
 * It was a billing report — plan, usage meters, plan features, period cost — and
 * nothing else, which made the landing page of a free product a statement about
 * money nobody is being asked for. The teaching summary is now the page, and the
 * billing cards render only where they mean something.
 */
export function OverviewPage(): ReactNode {
  const { orgId = '' } = useParams();
  const config = useClientConfig();

  const org = useAsync(() => api.get<Organization>(`/api/organizations/${orgId}`), [orgId]);
  /*
   * Guarded by the same flag that decides whether the cards render, so a
   * deployment with billing off makes none of these three requests. Listed in
   * the dependencies because the flag arrives from the server a moment after
   * first paint.
   */
  const billingOn = config.billingEnabled;
  const subscription = useAsync(
    () =>
      billingOn
        ? api.get<{ subscription: Subscription | null }>(
            `/api/organizations/${orgId}/subscription`,
          )
        : Promise.resolve({ subscription: null }),
    [orgId, billingOn],
  );
  const usage = useAsync(
    () =>
      billingOn
        ? api.get<{ periodStart: string; periodEnd: string; data: UsageEntry[] }>(
            `/api/organizations/${orgId}/usage`,
          )
        : Promise.resolve({ periodStart: '', periodEnd: '', data: [] as UsageEntry[] }),
    [orgId, billingOn],
  );
  const billing = useAsync(
    () =>
      billingOn
        ? api.get<BillingStatus>(`/api/organizations/${orgId}/billing`)
        : Promise.resolve(null),
    [orgId, billingOn],
  );

  const { run, pending, error: actionError } = useAction();

  if (org.loading) return <Loading />;
  if (org.error) {
    return (
      <div className="container">
        <ErrorBanner error={org.error} />
      </div>
    );
  }

  const current = subscription.data?.subscription ?? null;
  const entries = usage.data?.data ?? [];
  // Only metered services belong in a usage report. A feature entitlement has
  // no consumption to show, and rendering one as "0 of 3" would assert
  // something untrue.
  const metered = entries.filter((entry) => entry.enabled && entry.kind === 'metered');
  const features = entries.filter((entry) => entry.kind === 'feature');
  const includedFeatures = features.filter((entry) => entry.enabled);
  const withheldCount = features.length - includedFeatures.length;
  const canManage = org.data?.role === 'owner' || org.data?.role === 'admin';

  /**
   * Open the provider's customer portal.
   *
   * Offered only when the active provider has one — Razorpay does not, and the
   * endpoint answers UNSUPPORTED_OPERATION there. The window is opened from the
   * response rather than pre-rendered as a link because the URL is a
   * short-lived session that should not be minted on every page view.
   */
  async function openPortal(): Promise<void> {
    await run(async () => {
      const { url } = await api.post<{ url: string }>(`/api/organizations/${orgId}/billing/portal`);
      window.open(url, '_blank', 'noopener,noreferrer');
    });
  }

  async function cancel(atPeriodEnd: boolean): Promise<void> {
    await run(async () => {
      await api.delete(
        `/api/organizations/${orgId}/subscription?atPeriodEnd=${String(atPeriodEnd)}`,
      );
      subscription.reload();
      billing.reload();
    });
  }

  return (
    <div className="container">
      <div className="page-head">
        <div>
          <h1>{org.data?.name}</h1>
          <p className="mono faint">/{org.data?.slug}</p>
        </div>
      </div>

      <OrgNav orgId={orgId} />

      <div className="stack">
        <ErrorBanner message={actionError} />

        <TeachingSummary orgId={orgId} canManage={canManage} />

        {/*
          Billing, only where billing exists. With BILLING_DISABLED set there is
          no subscription to show, no metered usage to meter and no invoice to
          preview — four requests and four cards that all say "nothing", on the
          first page a teacher sees.
        */}
        {config.billingEnabled && (
          <>
          {/*
            A paid subscription that has not been paid for yet is the one state a
            user is most likely to be stuck in, so the resume link is surfaced
            prominently rather than buried in a billing page.
          */}
          {current?.status === 'incomplete' && billing.data?.checkoutUrl && (
            <div className="banner banner-warning">
              This subscription is waiting for payment authorisation.{' '}
              <a href={billing.data.checkoutUrl}>Complete payment</a> to activate it.
            </div>
          )}

          {current?.status === 'past_due' && (
            <div className="banner banner-warning">
              The last payment did not go through. Access continues for now, but the subscription will
              not renew until payment succeeds.
            </div>
          )}

          {current?.cancelAtPeriodEnd && (
            <div className="banner banner-warning">
              Scheduled to cancel on {formatDate(current.currentPeriodEnd)}. Access continues until
              then.
            </div>
          )}

          <Card
            title="Subscription"
            actions={
              canManage && (
                <Link to={`/orgs/${orgId}/plans`} className="button button-secondary">
                  {current ? 'Change plan' : 'Choose a plan'}
                </Link>
              )
            }
          >
            {subscription.loading ? (
              <Spinner />
            ) : current ? (
              <div className="stack stack-sm">
                <div className="row">
                  <strong style={{ textTransform: 'capitalize' }}>
                    {current.planSlug ?? 'Unknown plan'}
                  </strong>
                  <StatusBadge status={current.status} />
                </div>
                <p className="small muted">
                  Current period {formatDate(current.currentPeriodStart)} –{' '}
                  {formatDate(current.currentPeriodEnd)} · renews{' '}
                  {formatRelative(current.currentPeriodEnd)}
                </p>
                <p className="small faint">
                  Billed in {current.currency} via {current.provider}
                </p>

                {canManage && billing.data?.capabilities.customerPortal && (
                  <div className="button-row" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void openPortal()}
                    >
                      {pending && <Spinner />}
                      Manage payment method
                    </button>
                  </div>
                )}

                {canManage && current.status !== 'canceled' && !current.cancelAtPeriodEnd && (
                  <div className="button-row" style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="button-secondary"
                      disabled={pending}
                      onClick={() => void cancel(true)}
                    >
                      Cancel at period end
                    </button>
                    <button
                      type="button"
                      className="button-danger"
                      disabled={pending}
                      onClick={() => void cancel(false)}
                    >
                      Cancel immediately
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <p className="muted small">
                No subscription yet — this organization is on the free plan.{' '}
                {canManage && <Link to={`/orgs/${orgId}/plans`}>See the plans</Link>}
              </p>
            )}
          </Card>

          <Card title="Usage this period">
            {usage.loading ? (
              <Spinner />
            ) : metered.length === 0 ? (
              <p className="muted small">Nothing recorded yet.</p>
            ) : (
              <>
                <p className="small faint" style={{ marginBottom: '0.75rem' }}>
                  {usage.data && formatDate(usage.data.periodStart)} –{' '}
                  {usage.data && formatDate(usage.data.periodEnd)}
                </p>
                {metered.map((entry) => (
                  <div key={entry.serviceKey} className="usage-row">
                    <div className="usage-label">
                      <span>{entry.name}</span>
                      {entry.overageQuantity > 0 && (
                        <span className="badge badge-warning">
                          {formatNumber(entry.overageQuantity)} over
                        </span>
                      )}
                      <span className="usage-numbers">
                        {formatNumber(entry.quantity)}
                        {entry.limit === null ? '' : ` / ${formatNumber(entry.limit)}`}{' '}
                        {pluralise(entry.limit ?? entry.quantity, entry.unit)}
                      </span>
                    </div>
                    <UsageMeter quantity={entry.quantity} limit={entry.limit} unit={entry.unit} />
                  </div>
                ))}
              </>
            )}
          </Card>

          {features.length > 0 && (
            <Card title="Plan features">
              {/*
                Only what the tenant actually has. Listing every withheld feature
                turns this into a wall of "Not included" that buries the answer to
                the question being asked, which is "what do I have?".
              */}
              {includedFeatures.length > 0 ? (
                <table>
                  <tbody>
                    {includedFeatures.map((feature) => (
                      <tr key={feature.serviceKey}>
                        <td>{feature.name}</td>
                        <td style={{ textAlign: 'right' }}>
                          {feature.limit === null ? (
                            <span className="badge badge-success">Included</span>
                          ) : (
                            <span className="badge badge-success">
                              Up to {formatNumber(feature.limit)}{' '}
                              {pluralise(feature.limit, feature.unit)}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="muted small">This plan includes no additional features.</p>
              )}

              {withheldCount > 0 && (
                <p className="small faint" style={{ marginTop: '0.75rem' }}>
                  {withheldCount} more {withheldCount === 1 ? 'feature is' : 'features are'} available
                  on higher plans. <Link to={`/orgs/${orgId}/plans`}>Compare plans</Link>
                </p>
              )}
            </Card>
          )}

          {current && <CurrentPeriodCost orgId={orgId} />}
          </>
        )}
      </div>
    </div>
  );
}

/** Live cost of the open period. Reads the preview endpoint, which persists nothing. */
function CurrentPeriodCost({ orgId }: { orgId: string }): ReactNode {
  const preview = useAsync(
    () => api.get<InvoicePreview>(`/api/organizations/${orgId}/invoices/current`),
    [orgId],
  );

  if (preview.loading) return null;
  if (preview.error || !preview.data) return null;

  const billable = preview.data.lines.filter((line) => line.amountMinor > 0);

  return (
    <Card title="Cost so far this period">
      <p className="small faint" style={{ marginBottom: '0.75rem' }}>
        An estimate for the open period. It is not an invoice and nothing has been charged.
      </p>
      <table>
        <tbody>
          {billable.map((line, index) => (
            <tr key={index}>
              <td>{line.description}</td>
              <td className="num">{formatMoney(line.amountMinor, preview.data!.currency)}</td>
            </tr>
          ))}
          <tr>
            <td>
              <strong>Total</strong>
            </td>
            <td className="num">
              <strong>{formatMoney(preview.data.totalMinor, preview.data.currency)}</strong>
            </td>
          </tr>
        </tbody>
      </table>
    </Card>
  );
}
